import { inject } from '@angular/core';
import { signalStore, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { immerOn } from 'ngrx-immer/signals';
import { mapResponse } from '@ngrx/operators';
import { exhaustMap, switchMap } from 'rxjs';
import {
  PREDICTION_GAME_SERVICE,
  RankingFilterOption,
  rankingEvents,
  RankingPage,
  RankingUserDetail,
} from '@kikkz/shared/prediction-game/util';
import { SNACKBAR_SERVICE } from '@kikkz/shared/snackbar/util';
import { TRANSLATION_SERVICE } from '@kikkz/shared/i18n/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { serverErrorMessage } from './server-error-message';
import { asyncReducers } from '@kikkz/shared/state/util';
import { rankingFilterSequence } from './ranking-filter-sequence';

/**
 * Lifecycle of the ranking-user lookup behind the profile modal. Together with
 * {@link RankingState.rankingUserRequestId} it lets a consumer wait for exactly ITS
 * request to settle, without any `skip`-based guessing about what is already in the
 * signal at subscribe time.
 */
export type RankingUserStatus = 'idle' | 'loading' | 'success' | 'error';

export interface RankingState {
  /** Server-defined filter options for the global ranking, ordered by `orderIndex`. */
  rankingFilterOptions: RankingFilterOption[];
  isLoadingRankingFilterOptions: boolean;
  /** The option whose ranking is currently shown — owned by the store, not by the tab. */
  selectedRankingFilterOption: RankingFilterOption | null;
  ranking: RankingPage | null;
  isLoadingRanking: boolean;
  selectedRankingUser: RankingUserDetail | null;
  /** Not wired to any row-level spinner today — `kikkz-ranking-list-item` has no loading input; this flag is for a future per-row spinner. */
  isLoadingRankingUser: boolean;
  /** Incremented on every detail lookup, so a consumer can correlate the outcome. */
  rankingUserRequestId: number;
  rankingUserStatus: RankingUserStatus;
  /**
   * The ranking view's error — written by BOTH requests: without the server's filter
   * options there is no ranking request the backend would answer, so a failure of either
   * one is the view's error.
   */
  rankingError: string | null;
  /** The snackbar already surfaces this failure to the user; this field exists for state observability (devtools/tests), not because any component reads it. */
  rankingUserError: string | null;
}

const initialState: RankingState = {
  rankingFilterOptions: [],
  isLoadingRankingFilterOptions: false,
  selectedRankingFilterOption: null,
  ranking: null,
  isLoadingRanking: false,
  selectedRankingUser: null,
  isLoadingRankingUser: false,
  rankingUserRequestId: 0,
  rankingUserStatus: 'idle',
  rankingError: null,
  rankingUserError: null,
};

/**
 * Global ranking region: the server-driven filter options, the selected option, the
 * ranking page itself and the per-user detail lookup.
 *
 * The sequence "load options → preselect the first one → load its ranking" lives here as
 * a store effect, so every consumer of the ranking gets it for free and no tab has to
 * hold filter state of its own.
 */
export const RankingStore = signalStore(
  withState(initialState),
  withDevtools('prediction-game-ranking'),
  withReducer(
    // The options request opens the ranking view, and without the server's options there
    // is no ranking request the backend would answer (it matches the option verbatim) —
    // so this region writes the RANKING's error: cleared when the view opens (a retry must
    // not keep showing the stale error card while it is in flight), set when the options
    // fail, which lets `kikkz-async-content` offer the retry.
    ...asyncReducers<RankingState>()({
      load: rankingEvents.loadRankingFilterOptions,
      success: rankingEvents.loadRankingFilterOptionsSuccess,
      failed: rankingEvents.loadRankingFilterOptionsFailed,
      loading: 'isLoadingRankingFilterOptions',
      error: 'rankingError',
      onSuccess: (state, payload) => {
        state.rankingFilterOptions = payload.options;
      },
    }),
    immerOn(rankingEvents.selectFilterOption, (state, { payload }) => {
      state.selectedRankingFilterOption = payload.option;
    }),
    ...asyncReducers<RankingState>()({
      load: rankingEvents.loadRanking,
      success: rankingEvents.loadRankingSuccess,
      failed: rankingEvents.loadRankingFailed,
      loading: 'isLoadingRanking',
      error: 'rankingError',
      onSuccess: (state, payload) => {
        state.ranking = payload.ranking;
      },
    }),
    immerOn(rankingEvents.loadRankingUserDetail, (state) => {
      state.isLoadingRankingUser = true;
      state.rankingUserError = null;
      // Drop the previous user's detail right away: leaving it in place would show the
      // last-opened profile while the new one loads — and, if the new lookup fails, keep
      // showing the wrong person entirely.
      state.selectedRankingUser = null;
      state.rankingUserRequestId += 1;
      state.rankingUserStatus = 'loading';
    }),
    immerOn(rankingEvents.loadRankingUserDetailSuccess, (state, { payload }) => {
      state.selectedRankingUser = payload.detail;
      state.isLoadingRankingUser = false;
      state.rankingUserStatus = 'success';
    }),
    immerOn(rankingEvents.loadRankingUserDetailFailed, (state, { payload }) => {
      state.isLoadingRankingUser = false;
      state.selectedRankingUser = null;
      state.rankingUserError = payload.error;
      state.rankingUserStatus = 'error';
    }),
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      service = inject(PREDICTION_GAME_SERVICE),
      snackbar = inject(SNACKBAR_SERVICE),
      translation = inject(TRANSLATION_SERVICE),
      logger = inject(LOGGING_SERVICE),
    ) => {
      // "options arrived → preselect the first one → load its ranking", shared with the
      // group ranking (which adds a guard for the group the options belong to).
      const filterSequence = rankingFilterSequence({
        events,
        optionsLoaded: rankingEvents.loadRankingFilterOptionsSuccess,
        selectOption: rankingEvents.selectFilterOption,
        loadRanking: (option) => rankingEvents.loadRanking({ filterOption: option }),
      });

      return {
        loadRankingFilterOptions$: events.on(rankingEvents.loadRankingFilterOptions).pipe(
          exhaustMap(() =>
            service.loadRankingFilterOptions().pipe(
              mapResponse({
                next: (options) => rankingEvents.loadRankingFilterOptionsSuccess({ options }),
                error: (error: unknown) =>
                  rankingEvents.loadRankingFilterOptionsFailed({
                    error: serverErrorMessage(error, 'pg.errors.rankingFilterOptionsLoad', translation, logger),
                  }),
              }),
            ),
          ),
        ),
        selectInitialFilterOption$: filterSequence.selectInitialOption$,
        loadRankingOnSelect$: filterSequence.loadOnSelect$,
        loadRanking$: events.on(rankingEvents.loadRanking).pipe(
          switchMap(({ payload }) =>
            service.loadRanking(payload.filterOption).pipe(
              mapResponse({
                next: (ranking) => rankingEvents.loadRankingSuccess({ ranking }),
                error: (error: unknown) =>
                  rankingEvents.loadRankingFailed({
                    error: serverErrorMessage(error, 'pg.errors.rankingLoad', translation, logger),
                  }),
              }),
            ),
          ),
        ),
        loadRankingUserDetail$: events.on(rankingEvents.loadRankingUserDetail).pipe(
          switchMap(({ payload }) =>
            service.loadRankingUserDetail(payload.userId).pipe(
              mapResponse({
                next: (detail) => rankingEvents.loadRankingUserDetailSuccess({ detail }),
                error: (error: unknown) => {
                  const message = serverErrorMessage(error, 'pg.errors.userStatsLoad', translation, logger);
                  snackbar.error(message);
                  return rankingEvents.loadRankingUserDetailFailed({ error: message });
                },
              }),
            ),
          ),
        ),
      };
    },
  ),
);
